import Vue from 'vue';
import { gsap } from 'gsap';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

Vue.component('navigation-component', {
    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            showNavigation: false,
            openTl: null,
            hamburgerTl: null,
            activeItem: null,
            activeSubItems: null,
            activeSubLinks: null,
            menuIsAnimating: false,
        };
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.initHamburgTl();
        EventBus.$on('navigation-state-changed', ((visible) => {
            if (this.menuIsAnimating) {
                return;
            }

            this.menuIsAnimating = true;
            this.showNavigation = visible;

            if (visible) {
                this.openNavigation();
                disableBodyScroll(document.querySelector('.navigation__inner'), { reserveScrollBarGap: true });
                return;
            }

            this.closeNavigation();
            enableBodyScroll(document.querySelector('.navigation__inner'));
        }));
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        initHamburgTl() {
            const hamburgerLines = document.querySelectorAll('.sidebar__link--hamburger rect');
            this.hamburgerTl = gsap.timeline();

            this.hamburgerTl.set('.sidebar__icon', { overflow: 'visible' }, 0);
            this.hamburgerTl.to([hamburgerLines[0], hamburgerLines[2]], { scaleX: '0', duration: 0.4, ease: 'Power4.inOut' }, 0);
            this.hamburgerTl.set([hamburgerLines[0]], { scale: '1', y: 7 }, '>');

            this.hamburgerTl.to([hamburgerLines[0]], {
                rotate: 45, x: 0, y: 7, transformOrigin: '50%', duration: 0.4, ease: 'Power4.inOut',
            }, '>');

            this.hamburgerTl.to([hamburgerLines[1]], {
                rotate: -45, x: 0, y: 0, transformOrigin: '50%', duration: 0.4, ease: 'Power4.inOut',
            }, '<');

            this.hamburgerTl.pause();
        },

        initOpenTl() {
            this.openTl = gsap.timeline({
                onComplete: () => {
                    this.menuIsAnimating = false;
                },
            });

            this.openTl.fromTo('.navigation', { x: '-135%' }, { x: 0, ease: 'Power4.inOut', duration: 0.4 });
            this.openTl.add(this.hamburgerTl.play(), '<');

            this.openTl.fromTo('.navigation__link', { x: '-100' }, {
                x: 0, ease: 'Power2.out', duration: 1.2, stagger: 0.1,
            }, '<-0.2');

            this.openTl.fromTo('.navigation__link__text', { x: '-120%' }, {
                x: 0, ease: 'Power3.out', duration: 0.7, stagger: 0.1,
            }, '<');

            this.openTl.to('.navigation__link--active .navigation__link__line', { x: 0, ease: 'Power4.in', duration: 0.6 }, '<');

            if (this.activeSubItems && this.activeSubLinks) {
                this.openTl.fromTo(this.activeSubItems, { x: '-100' }, {
                    x: 0, ease: 'Power2.out', duration: 1, stagger: 0.1,
                }, '<-0.1');
                this.openTl.fromTo(this.activeSubLinks, { x: '-100%' }, {
                    x: 0, ease: 'Power3.out', duration: 0.7, stagger: 0.1,
                }, '<');
            }

            this.openTl.pause();
        },

        openNavigation() {
            this.activeItem = this.$el.querySelector('.navigation__link--active') ?? null;

            if (this.activeItem) {
                this.activeSubItems = this.activeItem.parentElement.querySelectorAll('.navigation__sublist-container');
                this.activeSubLinks = this.activeItem.parentElement.querySelectorAll('.navigation__sublink');
                gsap.set(this.activeSubLinks, { x: '-100%' });
                gsap.set(this.activeSubItems, { x: '-100' });
            }

            Vue.nextTick(() => {
                this.initOpenTl();
                this.openTl.play();
            });
        },

        closeNavigation() {
            this.closeTl = gsap.timeline({
                onComplete: () => {
                    this.menuIsAnimating = false;
                },
            });
            this.closeTl.add(this.hamburgerTl.reverse(), 0);
            this.closeTl.to('.navigation__link__text', {
                x: '-100%', ease: 'Power4.inOut', duration: 0.5,
            }, '<');

            this.closeTl.to('.navigation__sublink', {
                x: '-100%', ease: 'Power4.inOut', duration: 0.5,
            }, '<');

            this.closeTl.set('.navigation__link__line', { x: '-110%', ease: 'Power4.in', duration: 0.5 }, '<');
            this.closeTl.to('.navigation', { x: '150%', ease: 'Power4.in', duration: 0.6 }, '<');

            this.openTl.clear();
            this.openTl.kill();
            this.openTl = null;
        },

        toggleItem(event) {
            const activeLink = document.getElementsByClassName('navigation__link--active');
            const item = event.currentTarget;
            const subList = event.currentTarget.nextElementSibling;

            if (item.classList.contains('navigation__link--active')) {
                item.classList.remove('navigation__link--active');
                gsap.to(subList, 0.3, {
                    height: 0,
                    ease: 'Quad.easeOut',
                });
                gsap.set('.navigation__link__line', {
                    x: '-110%', ease: 'Power4.inOut', duration: 0.5,
                });
                return;
            }

            if (activeLink.length) {
                activeLink[0].classList.remove('navigation__link--active');
                gsap.to('.navigation__sublist-container', 0.1, {
                    height: 0,
                    ease: 'Quad.easeOut',
                });
                gsap.set('.navigation__link__line', {
                    x: '-110%', ease: 'Power4.inOut', duration: 0.5,
                });
            }

            item.classList.add('navigation__link--active');
            gsap.set('.navigation__sublink', {
                x: 0, ease: 'Power4.inOut', duration: 0.5,
            });
            gsap.set('.navigation__link--active .navigation__link__line', {
                x: 0, ease: 'Power4.inOut', duration: 0.5,
            });
            gsap.to(subList, 0.3, {
                height: 'auto',
                ease: 'Power4.out',
            });
        },
    },
});
